import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Box, Divider, Menu, MenuItem, MenuList, Paper, Popper, Typography } from '@mui/material'
import { type Dayjs } from 'dayjs'

import { type IconName } from '~components/icons/iconMapping'
import { accentColors, lightTokens, typography } from '~tokens/internal'

import Button from '../Button'
import Icon, { type SemanticIconName } from '../Icon'
import Search from '../Search'

import { type FormField } from './AnswerFilter'
import FilterCategoryPanel from './FilterCategoryPanel'
import './Filters.scss'
import { type FilterState, emptyFilterState } from './useFilterState'

export interface FilterRefinement {
  id: string
  label: string
  icon?: SemanticIconName | IconName
  options?: FilterOption[]
  node?: React.ReactNode
  formFields?: FormField[]
}

export type { FormField }

export interface ProcessStageField {
  id: string
  name: string
  order: number
  description?: string | null
  process: string
}

export interface FilterOption {
  id: string
  label: string
  /** Additional text used for search matching when it differs from label. */
  searchLabel?: string
  tooltip?: string
  icon?: SemanticIconName | IconName
  iconColor?: string
  node?: React.ReactNode
  avatar?: React.ReactNode
  refinements?: FilterRefinement[]
  stages?: ProcessStageField[]
  formFields?: FormField[]
}

export interface FilterDrilldown {
  id: string
  label: string
  icon?: SemanticIconName | IconName
  options: FilterOption[]
}

export interface FilterCategory {
  id: string
  label: string
  icon?: SemanticIconName | IconName
  group?: string
  type?: 'options' | 'date' | 'process' | 'text'
  options?: FilterOption[]
  drilldowns?: FilterDrilldown[]
  /** Renders an italicised "none" option at the top of the submenu (e.g. "No Manager Assigned"). */
  noneOption?: { id: string; label: string }
  /** For date categories: renders an "Overdue" checkbox with alert icon at the top of the date submenu. */
  overdueOption?: { id: string; label: string }
  /**
   * For `process` categories: locks the category to a single option (workflow)
   * and renders that option's refinements ("Refine by Stage" / "Refine by
   * Answers") at the top level of the submenu, without a workflow checkbox.
   */
  fixedOptionId?: string
  /** Show a "Reset Filter" button at the bottom of this category's submenu. Default false. */
  showReset?: boolean
  /** Hide the search input inside this category's submenu. Useful for categories with very few options. Default false. */
  hideSearch?: boolean
}

export interface DateRange {
  from: Dayjs | null
  to: Dayjs | null
}

export interface FilterHandle {
  openToCategory: (categoryId: string) => void
}

export interface FilterProps {
  categories: FilterCategory[]
  state?: FilterState
  onChange?: (state: FilterState) => void
  loading?: boolean
  displayMode?: 'menu' | 'dropdown'
}

const buildProcessRefinements = (
  stages: ProcessStageField[],
  formFields?: FormField[],
): FilterRefinement[] => {
  const refinements: FilterRefinement[] = [
    {
      id: 'stages',
      label: 'Refine by Stage',
      options: stages.map((stage) => ({
        id: stage.id,
        label: stage.name,
      })),
    },
  ]
  if (formFields && formFields.length > 0) {
    refinements.push({
      id: 'answers',
      label: 'Refine by Answers',
      formFields,
    })
  }
  return refinements
}

const Filter = React.forwardRef<FilterHandle, FilterProps>(
  ({ categories, state, onChange, loading, displayMode = 'menu' }, ref) => {
    const {
      selections = {},
      dateRanges = {},
      refinements = {},
      answerFilters = {},
    } = state ?? emptyFilterState

    // Main menu
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [searchQuery, setSearchQuery] = useState('')
    const open = Boolean(anchorEl)
    const triggerRef = useRef<HTMLDivElement>(null)

    const categoryTriggerRefs = useRef<Map<string, HTMLElement>>(new Map())

    // Submenu (hover)
    const [hoveredCategoryId, setHoveredCategoryId] = useState<string | null>(null)
    // When a refinement is surfaced as a top-level entry (fixed-workflow
    // Process page), this tracks which refinement panel to open directly.
    const [hoveredRefinement, setHoveredRefinement] = useState<{
      categoryId: string
      optionId: string
      refinement: FilterRefinement
    } | null>(null)
    const popoverPaperRef = useRef<HTMLDivElement | null>(null)
    const pendingCategoryRef = useRef<string | null>(null)
    const paperCallbackRef = useCallback((node: HTMLDivElement | null) => {
      popoverPaperRef.current = node
    }, [])

    useEffect(() => {
      if (open && pendingCategoryRef.current) {
        const id = setTimeout(() => {
          setHoveredCategoryId(pendingCategoryRef.current)
          pendingCategoryRef.current = null
        }, 200)
        return (): void => clearTimeout(id)
      }
    }, [open])

    useImperativeHandle(ref, () => ({
      openToCategory: (categoryId: string): void => {
        if (displayMode === 'dropdown') {
          const el = categoryTriggerRefs.current.get(categoryId)
          if (el) {
            setDropdownAnchorEl(el)
            setDropdownCategoryId(categoryId)
          }
        } else {
          if (triggerRef.current) {
            pendingCategoryRef.current = categoryId
            setAnchorEl(triggerRef.current)
          }
        }
      },
    }))

    const handleClose = useCallback(() => {
      setAnchorEl(null)
      setSearchQuery('')
      setHoveredCategoryId(null)
      setHoveredRefinement(null)
      pendingCategoryRef.current = null
    }, [])

    const handleCategoryMouseEnter = useCallback(
      (_e: React.MouseEvent<HTMLElement>, categoryId: string) => {
        setHoveredCategoryId(categoryId)
        setHoveredRefinement(null)
      },
      [],
    )

    const handleRefinementMouseEnter = useCallback(
      (categoryId: string, optionId: string, refinement: FilterRefinement) => {
        setHoveredCategoryId(categoryId)
        setHoveredRefinement({ categoryId, optionId, refinement })
      },
      [],
    )

    // Dropdown mode state: which category popover is open
    const [dropdownAnchorEl, setDropdownAnchorEl] = useState<HTMLElement | null>(null)
    const [dropdownCategoryId, setDropdownCategoryId] = useState<string | null>(null)

    const handleDropdownOpen = useCallback(
      (e: React.MouseEvent<HTMLElement>, categoryId: string) => {
        setDropdownAnchorEl(e.currentTarget)
        setDropdownCategoryId(categoryId)
      },
      [],
    )

    const handleDropdownClose = useCallback(() => {
      setDropdownAnchorEl(null)
      setDropdownCategoryId(null)
    }, [])

    const effectiveCategories = categories.map((cat) => {
      if (cat.type !== 'process' || !cat.options) return cat
      return {
        ...cat,
        options: cat.options.map((opt) => ({
          ...opt,
          icon: opt.icon ?? 'Sensors',
          iconColor: opt.iconColor ?? accentColors.green.main,
          refinements:
            opt.refinements ??
            (opt.stages ? buildProcessRefinements(opt.stages, opt.formFields) : undefined),
        })),
      }
    })

    // Categories that actually have values to show
    const categoriesWithValues = effectiveCategories.filter(
      (cat) =>
        cat.type === 'date' ||
        cat.type === 'text' ||
        (cat.options && cat.options.length > 0) ||
        (cat.drilldowns && cat.drilldowns.length > 0),
    )

    const filteredCategories = ((): FilterCategory[] => {
      if (!searchQuery.trim()) return effectiveCategories
      const q = searchQuery.toLowerCase()
      return effectiveCategories.filter((c) => c.label.toLowerCase().includes(q))
    })()

    const groupedCategories = ((): { label: string; cats: FilterCategory[] }[] => {
      const groups: { label: string; cats: FilterCategory[] }[] = []
      const groupMap = new Map<string, FilterCategory[]>()
      for (const cat of filteredCategories) {
        const key = cat.group ?? ''
        if (!groupMap.has(key)) {
          const arr: FilterCategory[] = []
          groupMap.set(key, arr)
          groups.push({ label: key, cats: arr })
        }
        groupMap.get(key)!.push(cat)
      }
      return groups
    })()

    const activeCategory = effectiveCategories.find((c) => c.id === hoveredCategoryId) ?? null

    const getAnswerFilterCount = (categoryId: string, optionId: string): number =>
      (answerFilters[categoryId]?.[optionId] ?? []).filter((f) => f.applied).length

    /** Count selections for a single refinement of an option (stages or answers). */
    const getRefinementCount = (
      categoryId: string,
      optionId: string,
      refinement: FilterRefinement,
    ): number => {
      if (refinement.formFields) return getAnswerFilterCount(categoryId, optionId)
      return (refinements[categoryId]?.[optionId]?.[refinement.id] ?? []).length
    }

    /** Count children (refinements + answer filters) for a single selected option. */
    const getOptionChildCount = (categoryId: string, optionId: string): number => {
      let childCount = 0
      const optRef = refinements[categoryId]?.[optionId]
      if (optRef) {
        for (const ids of Object.values(optRef)) childCount += ids.length
      }
      childCount += getAnswerFilterCount(categoryId, optionId)
      return childCount
    }

    /** Count active date filters (range counts as 1, overdue selections counted individually). */
    const getDateCategoryCount = (categoryId: string): number => {
      let count = 0
      const range = dateRanges[categoryId]
      if (range?.from || range?.to) count += 1
      count += selections[categoryId]?.length ?? 0
      return count
    }

    const getCategoryCount = (categoryId: string): number => {
      const cat = effectiveCategories.find((c) => c.id === categoryId)
      if (cat?.type === 'text') return (state?.textValues?.[categoryId] ?? '').length > 0 ? 1 : 0
      if (cat?.type === 'date') return getDateCategoryCount(categoryId)

      // Fixed (workflow-page) process category: the option is implied, so count
      // its refinement / answer children directly.
      if (cat?.type === 'process' && cat.fixedOptionId) {
        return getOptionChildCount(categoryId, cat.fixedOptionId)
      }

      const selectedIds = selections[categoryId] ?? []
      let count = 0
      for (const optionId of selectedIds) {
        const childCount = getOptionChildCount(categoryId, optionId)
        // If children are selected, count only the children (leaf-only);
        // otherwise the parent selection itself counts as 1.
        count += childCount > 0 ? childCount : 1
      }
      return count
    }

    const totalFilterCount = useMemo(() => {
      let count = 0
      for (const cat of effectiveCategories) {
        count += getCategoryCount(cat.id)
      }
      return count
    }, [selections, refinements, answerFilters, dateRanges, effectiveCategories, state?.textValues])

    if (loading || categoriesWithValues.length === 0) return <></>

    // -----------------------------------------------------------------------
    // Dropdown mode: horizontal row of per-category dropdown triggers
    // -----------------------------------------------------------------------
    if (displayMode === 'dropdown') {
      const openCategory = effectiveCategories.find((c) => c.id === dropdownCategoryId) ?? null

      return (
        <Box className='filter-dropdown-row'>
          {categoriesWithValues.map((cat) => {
            const count = getCategoryCount(cat.id)
            return (
              <Box
                key={cat.id}
                className='filter-dropdown-trigger-wrapper'
                ref={(el: HTMLDivElement | null) => {
                  if (el) categoryTriggerRefs.current.set(cat.id, el)
                  else categoryTriggerRefs.current.delete(cat.id)
                }}
              >
                <Button
                  variant='outlined'
                  endIcon={<Icon name='expandMore' color={lightTokens.text.secondary} />}
                  onClick={(e) => handleDropdownOpen(e, cat.id)}
                  className='filter-dropdown-trigger'
                >
                  {cat.label}
                </Button>
                {count > 0 && <Box className='filter-badge filter-badge--positioned'>{count}</Box>}
              </Box>
            )
          })}
          <Menu
            open={Boolean(dropdownAnchorEl) && Boolean(dropdownCategoryId)}
            anchorEl={dropdownAnchorEl}
            onClose={handleDropdownClose}
            disableAutoFocus
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
              paper: {
                className: `filter-paper${openCategory?.type === 'date' ? ' filter-paper--date' : ''}`,
              },
            }}
          >
            {openCategory && onChange && (
              <FilterCategoryPanel
                key={openCategory.id}
                category={openCategory}
                state={state ?? emptyFilterState}
                onChange={onChange}
              />
            )}
          </Menu>
        </Box>
      )
    }

    // -----------------------------------------------------------------------
    // Menu mode (default): single "Filter" button with nested category list
    // -----------------------------------------------------------------------
    return (
      <>
        <Box className='filter-trigger' ref={triggerRef}>
          <Button
            variant='outlined'
            startIcon={<Icon name='filter' />}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            className='filter-trigger-button'
          >
            Filter
          </Button>
          {totalFilterCount > 0 && (
            <Box className='filter-badge filter-badge--positioned'>{totalFilterCount}</Box>
          )}
        </Box>
        <Menu
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              ref: paperCallbackRef,
              className: 'filter-paper filter-paper--main',
            },
          }}
        >
          <Box className='filter-search-wrapper' onKeyDown={(e) => e.stopPropagation()}>
            <Search
              placeholder='Find a filter'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClear={() => setSearchQuery('')}
            />
          </Box>
          {groupedCategories.map((group, gi) => (
            <Box key={group.label || `ungrouped-${gi}`} className='filter-category-group'>
              {group.label && (
                <Typography
                  variant='overline'
                  className='filter-group-header'
                  sx={{
                    color: 'text.secondary',
                    lineHeight: '2',
                  }}
                >
                  {group.label}
                </Typography>
              )}
              <Box className='filter-category-group-items'>
                {group.cats.map((cat) => {
                  const showDivider =
                    group.cats.indexOf(cat) < group.cats.length - 1 ||
                    gi < groupedCategories.length - 1

                  // Fixed (workflow-page) process category: surface the implied
                  // workflow's refinements ("Refine by Stage" / "Refine by
                  // Answers") as top-level entries, with no workflow pop-out.
                  const fixedOption =
                    cat.type === 'process' && cat.fixedOptionId
                      ? cat.options?.find((o) => o.id === cat.fixedOptionId)
                      : undefined
                  if (fixedOption) {
                    return (
                      <React.Fragment key={cat.id}>
                        {(fixedOption.refinements ?? []).map((ref) => {
                          const count = getRefinementCount(cat.id, fixedOption.id, ref)
                          return (
                            <MenuItem
                              key={ref.id}
                              className='filter-menu-item'
                              onMouseEnter={() =>
                                handleRefinementMouseEnter(cat.id, fixedOption.id, ref)
                              }
                            >
                              <Box className='filter-item-content'>
                                {ref.icon && (
                                  <Icon
                                    name={ref.icon}
                                    size={typography.fontSize.lg}
                                    color={lightTokens.text.primary}
                                  />
                                )}
                                <Typography variant='body2'>{ref.label}</Typography>
                              </Box>
                              {count > 0 && <Box className='filter-badge'>{count}</Box>}
                              <Icon
                                name='chevronRight'
                                size={typography.fontSize.lg}
                                color={lightTokens.text.secondary}
                              />
                            </MenuItem>
                          )
                        })}
                        {showDivider ? <Divider className='filter-divider' /> : null}
                      </React.Fragment>
                    )
                  }

                  return (
                    <React.Fragment key={cat.id}>
                      <MenuItem
                        className='filter-menu-item'
                        onMouseEnter={(e) => handleCategoryMouseEnter(e, cat.id)}
                      >
                        <Box className='filter-item-content'>
                          {cat.icon && (
                            <Icon
                              name={cat.icon}
                              size={typography.fontSize.lg}
                              color={lightTokens.text.primary}
                            />
                          )}
                          <Typography variant='body2'>{cat.label}</Typography>
                        </Box>
                        {getCategoryCount(cat.id) > 0 && (
                          <Box className='filter-badge'>{getCategoryCount(cat.id)}</Box>
                        )}
                        <Icon
                          name='chevronRight'
                          size={typography.fontSize.lg}
                          color={lightTokens.text.secondary}
                        />
                      </MenuItem>
                      {showDivider ? <Divider className='filter-divider' /> : null}
                    </React.Fragment>
                  )
                })}
              </Box>
            </Box>
          ))}
          {filteredCategories.length === 0 && (
            <Typography variant='body2' className='filter-no-results'>
              No results
            </Typography>
          )}
        </Menu>
        <Popper
          open={
            open &&
            Boolean(popoverPaperRef.current) &&
            Boolean(hoveredCategoryId) &&
            Boolean(
              activeCategory?.type === 'date' ||
              activeCategory?.type === 'text' ||
              (activeCategory?.options?.length ?? 0) > 0 ||
              (activeCategory?.drilldowns?.length ?? 0) > 0,
            )
          }
          anchorEl={popoverPaperRef.current}
          placement='right-start'
          style={{ zIndex: 1300 }}
          modifiers={[
            { name: 'flip', enabled: true },
            {
              name: 'preventOverflow',
              enabled: true,
              options: { boundary: 'viewport', padding: 100 },
            },
          ]}
        >
          <Paper
            className={`filter-paper${activeCategory?.type === 'date' ? ' filter-paper--date' : ''}`}
          >
            <MenuList>
              {activeCategory && onChange && (
                <FilterCategoryPanel
                  key={activeCategory.id}
                  category={activeCategory}
                  state={state ?? emptyFilterState}
                  onChange={onChange}
                  initialRefinement={
                    hoveredRefinement?.categoryId === activeCategory.id
                      ? {
                          optionId: hoveredRefinement.optionId,
                          refinement: hoveredRefinement.refinement,
                        }
                      : undefined
                  }
                />
              )}
            </MenuList>
          </Paper>
        </Popper>
      </>
    )
  },
)

export default Filter
